'use client';

import { validateEmailFormat } from '@lib/util/forms';
import { FormErrorMessage } from '@ui-components/components/Form/FormErrorMessage';
import Image from 'next/image';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Dropdown, Input } from 'ui-components';
import { countries } from 'utils/src/countries';
import xss from 'xss';

type EmailSignupProps = {
  submitForm: (email: string, country: string) => Promise<void>;
  defaultCountryCode?: string;
  className?: string;
  heading: string;
  inputPlaceholder: string;
  inputClearLabel?: string;
  inputErrorLabel: string;
  checkboxLabel: string;
  checkboxErrorLabel: string;
  countryDropdownLabel: string;
  countryDropdownErrorLabel: string;
  submitButtonLabel: string;
  submittedHeading: string;
  submittedText: string;
  legalText: string;
  unexpectedErrorLabel: string;
  backgroundImageUrl?: string;
  enterEmailPersistentLabel: string;
  selectCountryPersistentLabel: string;
};

type EmailSignupErrors = {
  input: null | string;
  checkbox: null | string;
  dropdown: null | string;
  formSubmission: null | string;
};

const defaultErrorStates = {
  input: null,
  checkbox: null,
  dropdown: null,
  formSubmission: null,
};

export const EmailSignup = ({
  submitForm,
  defaultCountryCode,
  heading,
  inputPlaceholder,
  inputClearLabel,
  inputErrorLabel,
  checkboxLabel,
  checkboxErrorLabel,
  countryDropdownLabel,
  countryDropdownErrorLabel,
  submitButtonLabel,
  legalText,
  submittedHeading,
  submittedText,
  unexpectedErrorLabel,
  backgroundImageUrl,
  enterEmailPersistentLabel = 'Email',
  selectCountryPersistentLabel = 'Country',
}: EmailSignupProps) => {
  const defaultCountryDropdownItem = countries.find(
    (country) => country.value === defaultCountryCode
  );
  const [email, setEmail] = useState('');
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [errors, setErrors] = useState<EmailSignupErrors>(defaultErrorStates);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [activeCountry, setActiveCountry] = useState(
    defaultCountryDropdownItem ?? {
      value: 'default',
      label: countryDropdownLabel || 'Select country',
    }
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);
  const successRef = useRef<HTMLDivElement | null>(null);

  const handleInputFocus = () => {
    inputRef.current?.focus();
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let newErrors: EmailSignupErrors = defaultErrorStates;

    if (!validateEmailFormat(email)) {
      newErrors = {
        ...newErrors,
        input: inputErrorLabel,
      };
      handleInputFocus();
    }

    if (countryDropdownLabel && activeCountry.value === 'default') {
      newErrors = {
        ...newErrors,
        dropdown: countryDropdownErrorLabel || 'Please select a country',
      };
    }

    if (!isCheckboxChecked) {
      newErrors = {
        ...newErrors,
        checkbox: checkboxErrorLabel,
      };
      if (!newErrors.input) {
        checkboxRef.current?.focus();
      }
    }

    if (Object.values(newErrors).some((error) => error)) {
      setErrors(newErrors);
    } else {
      try {
        await submitForm(email, activeCountry.value);
        setIsFormSubmitted(true);
      } catch (error) {
        setErrors({
          ...defaultErrorStates,
          formSubmission: error.message,
        });
      }
    }
  };

  const handleOnClear = () => {
    setEmail('');
    handleInputFocus();
  };

  useEffect(() => {
    if (isFormSubmitted && successRef.current) {
      successRef.current.focus();
    }
  }, [isFormSubmitted]);

  return (
    <div className="relative flex min-h-96 flex-col justify-center overflow-hidden bg-black py-14">
      {backgroundImageUrl && (
        <Image
          fill
          alt=""
          src={backgroundImageUrl}
          className="pointer-events-none scale-110 object-cover opacity-20 blur-md"
        />
      )}
      <div className="main-grid relative z-10">
        {isFormSubmitted ? (
          <div
            data-test="email-signup-form-submitted"
            className="col-span-4 md:col-span-10 md:col-start-2 lg:col-span-7 lg:col-start-3"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            tabIndex={-1}
            ref={successRef}
            aria-labelledby="form-success-heading form-success-content"
          >
            <h2
              id="form-success-heading"
              className="header-mid mb-8 text-balance text-white"
            >
              {submittedHeading}
            </h2>
            <div
              id="form-success-content"
              className="body text-white"
              dangerouslySetInnerHTML={{ __html: xss(submittedText) }}
            />
          </div>
        ) : (
          <form
            data-test="email-signup-form"
            className="col-span-4 md:col-span-10 md:col-start-2 lg:col-span-7 lg:col-start-3"
            // rely on our validation rather than the built in HTML form validation that doesn't always work as expected
            noValidate
            onSubmit={onSubmit}
          >
            <h2 className="header-mid mb-8 text-balance text-white">
              {heading}
            </h2>
            <div className="flex flex-col flex-wrap md:flex-row">
              <Input
                id="newsletter-signup-email"
                name="newsletter-signup-email"
                placeholder={inputPlaceholder}
                accessibleLabel={`${enterEmailPersistentLabel}*`}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onClear={handleOnClear}
                clearInputLabel={inputClearLabel}
                error={errors.input ? inputErrorLabel : undefined}
                className="order-first mb-8 md:w-110 xl:w-116"
                ref={inputRef}
                aria-required="true"
                required
              />
              {countryDropdownLabel && (
                <Dropdown
                  id="newsletter-signup"
                  options={countries.map((country) => ({
                    value: country.value,
                    label: country.label,
                  }))}
                  selectedOption={
                    activeCountry && {
                      value: activeCountry.value,
                      label: activeCountry.label,
                    }
                  }
                  onSelect={(option) => setActiveCountry(option)}
                  maxDropdownHeight={170}
                  error={
                    errors.dropdown ? countryDropdownErrorLabel : undefined
                  }
                  className="relative z-10 mb-8 w-full md:w-110 xl:w-116"
                  dropdownStyle="newsletter"
                  label={`${selectCountryPersistentLabel}*`}
                  isRequired
                />
              )}
              <Checkbox
                id="newsletter-signup-checkbox"
                label={checkboxLabel}
                isChecked={isCheckboxChecked}
                onChange={() => setIsCheckboxChecked(!isCheckboxChecked)}
                error={errors.checkbox ? checkboxErrorLabel : undefined}
                className="mb-8 w-full"
                ref={checkboxRef}
              />
              {errors.formSubmission && (
                <FormErrorMessage
                  inputId="form"
                  errorMessage={unexpectedErrorLabel}
                  className="mt-4 w-full"
                />
              )}
              <div
                className="tiny-text [&_a]:tiny-text-link order-last mb-8 w-full text-grey-400"
                dangerouslySetInnerHTML={{ __html: xss(legalText) }}
              />
              <Button
                type="submit"
                size="medium"
                label={submitButtonLabel}
                className="order-1 mb-8 h-fit flex-1 md:order-first md:ml-3 md:mt-6 md:flex-none"
              />
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
